import { useEffect } from "react";
import TextContainer from "../components/textContainer";
import styled from "styled-components";

const StyledEvents = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-bottom: 230px;

    @media (max-width: 768px) {
        margin-bottom: 70px;
    
        h1 {
            font-size: 1.8rem;
            margin: 0 0 20px;
        }
    }
`;

const EventsTitle = styled.h1`
    font-size: 3rem;
    text-align: center;
    margin-bottom: 40px;
    color: #f8f6f6;
    font-family: 'Sedgwick Ave', cursive;
    position: relative;

    @media (max-width: 768px) {
        font-size: 1.8rem;
        margin: 0 0 20px;
    }
`;

const EventsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
`;

const EventCard = styled.div`
    background: rgba(38, 38, 38, 0.76);
    border-radius: 8px;
    padding: 20px;
    box-shadow: 1px 1px 4px 0px black;
    backdrop-filter: blur(5px);
    border-left: 4px solid #d2cccc;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(196, 150, 4, 0.1), transparent);
        transition: left 0.5s ease;
    }

    &:hover {
        transform: translateY(-3px);
        box-shadow: 2px 6px 12px 0px rgba(0, 0, 0, 0.6);
        border-left-color: rgba(196, 150, 4, 0.8);
        
        &::before {
            left: 100%;
        }
    }

    @media (max-width: 768px) {
        padding: 15px;
        
        &:hover {
            transform: translateY(-1px);
            box-shadow: 1px 3px 8px 0px rgba(0, 0, 0, 0.4);
        }
    }
`;

const EventTitle = styled.h2`
    font-size: 1.8rem;
    margin: 0 0 15px 0;
    color: #f8f6f6;
    font-family: 'Sedgwick Ave', cursive;
    position: relative;
    z-index: 1;

    @media (max-width: 768px) {
        font-size: 1.4rem;
        margin-bottom: 12px;
    }
`;

const EventDateTime = styled.div`
    font-size: 1.2rem;
    color: #d2cccc;
    margin-bottom: 12px;
    font-weight: bold;
    position: relative;
    z-index: 1;
    
    &::before {
        content: '📅';
        margin-right: 8px;
        opacity: 0.8;
    }

    @media (max-width: 768px) {
        font-size: 1rem;
        margin-bottom: 10px;
    }
`;

const EventLocation = styled.div`
    font-size: 1.1rem;
    color: #d2cccc;
    margin-bottom: 15px;
    font-style: italic;
    position: relative;
    z-index: 1;

    @media (max-width: 768px) {
        font-size: 0.9rem;
        margin-bottom: 12px;
    }
`;

const LocationLink = styled.a`
    color: inherit;
    text-decoration: none;
    transition: all 0.3s ease;
    position: relative;
    padding: 2px 4px;
    border-radius: 3px;
    
    &:hover {
        color: #f8f6f6;
        background: rgba(196, 150, 4, 0.2);
        transform: translateX(2px);
    }
    
    &:active {
        transform: translateX(1px);
    }
`;

const EventDescription = styled.p`
    font-size: 1rem;
    color: #f8f6f6;
    line-height: 1.6;
    margin: 15px 0 0 0;
    text-align: left;
    opacity: 0.9;
    position: relative;
    z-index: 1;

    @media (max-width: 768px) {
        font-size: 0.9rem;
        line-height: 1.5;
        margin-top: 12px;
    }
`;


const LoadingMessage = styled.p`
    font-size: 1.2rem;
    color: #d2cccc;
    text-align: center;
    margin: 40px 0;
    padding: 20px;
    background: rgba(38, 38, 38, 0.76);
    border-radius: 8px;
    box-shadow: 1px 1px 4px 0px black;
    backdrop-filter: blur(5px);
    animation: pulse 1.5s ease-in-out infinite;

    @keyframes pulse {
        0%, 100% { opacity: 0.7; }
        50% { opacity: 1; }
    }
`;

const NoEventsMessage = styled.p`
    font-size: 1.2rem;
    color: #d2cccc;
    text-align: center;
    margin: 40px 0;
    padding: 30px;
    background: rgba(38, 38, 38, 0.76);
    border-radius: 8px;
    box-shadow: 1px 1px 4px 0px black;
    backdrop-filter: blur(5px);
    font-style: italic;
    border-left: 4px solid rgba(196, 150, 4, 0.6);
`;

const ErrorMessage = styled.div`
    font-size: 1.1rem;
    color: #ff6b6b;
    text-align: center;
    margin: 40px 0;
    padding: 20px;
    background: rgba(255, 107, 107, 0.15);
    border-radius: 8px;
    border: 1px solid rgba(255, 107, 107, 0.4);
    box-shadow: 1px 1px 4px 0px black;
    backdrop-filter: blur(5px);
    
    strong {
        color: #ff8a8a;
    }
    
    small {
        color: #ffcccc;
        font-style: italic;
    }
`;

export default function Events({ events = [], loading = false, error = null }) {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const createGoogleMapsLink = (location) => {
        if (!location) return null;
        const encodedLocation = encodeURIComponent(location);
        return `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`;
    };

    const formatEventDateTime = (startObj, endObj) => {
        // Handle both dateTime and date-only formats
        const startDateTime = startObj?.dateTime || startObj?.date;
        const endDateTime = endObj?.dateTime || endObj?.date;
        
        if (!startDateTime) {
            return { date: 'Date TBD', time: '' };
        }
        
        const start = new Date(startDateTime);
        const end = endDateTime ? new Date(endDateTime) : null;
        
        const dateOptions = { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        };
        
        const timeOptions = { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
        };
        
        const dateStr = start.toLocaleDateString('en-US', dateOptions);
        
        // If it's a date-only event (no time), don't show time
        if (!startObj?.dateTime) {
            return {
                date: dateStr,
                time: 'All Day'
            };
        }
        
        const startTimeStr = start.toLocaleTimeString('en-US', timeOptions);
        const endTimeStr = end ? end.toLocaleTimeString('en-US', timeOptions) : '';
        
        return {
            date: dateStr,
            time: endTimeStr ? `${startTimeStr} - ${endTimeStr}` : startTimeStr
        };
    };

    return (
        <StyledEvents>
            <TextContainer>
                <EventsTitle>Upcoming Events</EventsTitle>
                
                {loading ? (
                    <LoadingMessage>Loading upcoming events...</LoadingMessage>
                ) : error ? (
                    <ErrorMessage>
                        <strong>Error loading events:</strong><br />
                        {error}<br />
                        <small>Please try refreshing the page or check back later.</small>
                    </ErrorMessage>
                ) : events.length === 0 ? (
                    <NoEventsMessage>
                        No upcoming events scheduled at this time. Check back soon!
                    </NoEventsMessage>
                ) : (
                    <EventsList>
                        {events.map(event => {
                            const dateTime = formatEventDateTime(event.start, event.end);
                            return (
                                <EventCard key={event.id}>
                                    <EventTitle>{event.summary}</EventTitle>
                                    <EventDateTime>
                                        {dateTime.date}
                                        <br />
                                        {dateTime.time}
                                    </EventDateTime>
                                    {event.location && (
                                        <EventLocation>
                                            📍 <LocationLink 
                                                href={createGoogleMapsLink(event.location)} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                title="Open in Google Maps"
                                            >
                                                {event.location}
                                            </LocationLink>
                                        </EventLocation>
                                    )}
                                    {event.description && (
                                        <EventDescription>{event.description}</EventDescription>
                                    )}
                                </EventCard>
                            );
                        })}
                    </EventsList>
                )}
            </TextContainer>
        </StyledEvents>
    );
}